import { FC } from 'react';
import {
  WellReviewMatch as SharedWellReviewMatch,
  type WellReviewConfig,
} from 'pages/documents/structuring/well-review-match';
import type { StagedWell, WellListItem } from './types';
import {
  confirmMatchAtom,
  createWellDetailResourceAtom,
  removeWellAtom,
  rerunSuggestionsAtom,
  restoreWellAtom,
  revertMatchAtom,
  wellDetailUrl,
} from './atoms/wells';
import { WellEditPanel } from './well-edit-panel';
import { ChangeMatchPanel } from './change-match-panel';
import { AgentAuditLog } from './agent-audit-log';

// AFE Well Match Review — a thin doctype wrapper over the shared drill-in
// (pages/documents/structuring/well-review-match). All behavior lives in the
// shared component; this file only supplies the AFE wiring: the AFE wells atom
// bundle, the AFE staged-tile field set (formation / basin / depths / locations
// / quarter / abstract / prospect), the HATEOAS-preferring removal + edit hrefs,
// the `.afe-structuring-shell` slide-in selector, the AFE Edit/ChangeMatch
// panels, and the AFE activity log (scoped by afeTargetId).

// Stable module-level config — a fresh object each render would re-create the
// detail resource atom (via useMemo) and refetch on every render.
const AFE_WELL_REVIEW_CONFIG: WellReviewConfig<StagedWell> = {
  createWellDetailResourceAtom,
  wellDetailUrl,
  confirmMatchAtom,
  revertMatchAtom,
  rerunSuggestionsAtom,
  removeWellAtom,
  restoreWellAtom,
  // Field order mirrors the AI Suggestion tile so shared rows line up
  // vertically. Hole Direction / Well Status aren't extracted from documents
  // (they come from the well registry), so they always render '—'. Quarter /
  // SHL / BHL and the planned depths always render (row-count stability);
  // FTP / LTP / Abstract / Prospect / Basin render only when present.
  stagedTileFields: (staged) => [
    { label: 'UWI / API Number', value: staged.uwiApi ?? '—' },
    { label: 'Hole Direction', value: '—' },
    { label: 'Well Status', value: '—' },
    { label: 'County/State', value: staged.countyState ?? '—' },
    { label: 'Formation', value: staged.formation ?? '—' },
    { label: 'Legal Description', value: staged.legalDescription ?? '—' },
    { label: 'Quarter / Subdivision', value: staged.quarterSubdivision || '—' },
    { label: 'SHL (Surface Hole Location)', value: staged.shl || '—' },
    { label: 'BHL (Bottom Hole Location)', value: staged.bhl || '—' },
    ...(staged.ftp
      ? [{ label: 'FTP (First Take Point)', value: staged.ftp }]
      : []),
    ...(staged.ltp
      ? [{ label: 'LTP (Last Take Point)', value: staged.ltp }]
      : []),
    ...(staged.abstractSurvey
      ? [{ label: 'Abstract / Survey', value: staged.abstractSurvey }]
      : []),
    ...(staged.prospect ? [{ label: 'Prospect', value: staged.prospect }] : []),
    ...(staged.basin ? [{ label: 'Basin', value: staged.basin }] : []),
    { label: 'Planned True Vertical Depth', value: staged.tvd || '—' },
    { label: 'Planned True Measured Depth', value: staged.tmd || '—' },
    { label: 'Planned True Lateral Length', value: staged.tll || '—' },
  ],
  // Prefer the HATEOAS removal href; fall back to the canonical DELETE route.
  removalUrl: (detail, detailHref) =>
    detail?.resources?.removal?.href ?? detailHref,
  wellEditHref: (detail) => detail?.resources?.update?.href,
  shellSelector: '.afe-structuring-shell',
  WellEditPanel,
  ChangeMatchPanel,
  renderAuditLog: (documentId, wellId) => (
    <AgentAuditLog
      documentId={documentId}
      section="well"
      afeTargetId={wellId}
    />
  ),
};

interface Props {
  documentId: number | string;
  /** The overview row for this well (gives us the id + HATEOAS hrefs). */
  well: WellListItem;
  index: number;
  /** Total well count (kept for a stable export signature). */
  total: number;
  /** Refetch the overview list after any mutation. */
  onMutated: () => void;
}

export const WellReviewMatch: FC<Props> = (props) => (
  <SharedWellReviewMatch<StagedWell>
    {...props}
    config={AFE_WELL_REVIEW_CONFIG}
  />
);
